import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useExchangeRates } from '../../../hooks/requests/exchange-rates/use-exchange-rates';
import { useInvoicesOverviewsNew } from '../../../hooks/requests/use-invoices-overviews/use-invoices-overviews-new';
import { useInvoiceStatistics } from '../../../hooks/requests/use-invoices-statistics';
import { useTablePagination } from '../../../hooks/use-table-pagination';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { InvoicesPageInfoCards } from '../../invoices/InvoicesPage/invoices-page-components/InvoicesPageInfoCards';
import { CustomerInvoicesSkeleton } from './customer-invoices-page-components/customer-invoices-skeleton';
import { CUSTOMER_INVOICES_TABLE_DEFAULT_PAGE_SIZE } from './customer-invoices-page-consts/customer-invoices-table-page-size';
import { CustomerInvoicesPageContentContainer } from './containers/CustomerInvoicesPageContentContainer';
import { Box, Stack } from '@mui/material';

const CustomerInvoicesPage: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');

  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination({
    defaultPageSize: CUSTOMER_INVOICES_TABLE_DEFAULT_PAGE_SIZE,
  });

  const { data: invoicesStatistics } = useInvoiceStatistics(customerId);

  const { data: invoices, isFetching: isFetchingInvoicesOverviews } = useInvoicesOverviewsNew(
    {
      customerId,
      page,
      pageSize,
    },
    { keepPreviousData: true },
  );

  const { data: exchangeRates } = useExchangeRates();

  const invoicesCount = invoices?.count ?? 0;

  return (
    <Suspense fallback={<CustomerInvoicesSkeleton />}>
      <Helmet>
        <title>Customer Invoices</title>
      </Helmet>
      <Stack
        sx={{
          gap: 1,
          height: '100%',
          minHeight: 0
        }}>
        <InvoicesPageInfoCards invoicesOverviewsSummary={invoicesStatistics?.summary} />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <CustomerInvoicesPageContentContainer
            customerId={customerId}
            invoices={invoices?.invoicesOverviews ?? []}
            invoicesCount={invoicesCount}
            loadingInvoices={isFetchingInvoicesOverviews}
            exchangeRates={exchangeRates}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Box>
      </Stack>
    </Suspense>
  );
};

const CustomerInvoicesWithGlobalSearchParams = withGlobalSearchParams(CustomerInvoicesPage);

const CustomerInvoicesPageWithSuspenseBoundary = withSuspenseBoundary(
  CustomerInvoicesWithGlobalSearchParams,
  {
    suspenseProps: {
      fallback: <CustomerInvoicesSkeleton />,
    },
  },
);

export { CustomerInvoicesPageWithSuspenseBoundary as CustomerInvoicesPage };
